import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import {
  getTermsApi,
  deleteTermsApi,
  addOrUpdateTermsApi as saveTermsApi
} from "../../../../services/Web/webTerms.service"

import { toastError, toastSuccess } from "../../../../utility/toastutill"

export const fetchTerms = createAsyncThunk(
  "terms/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getTermsApi()
      console.log('Fetched Terms Response:', response)
      return response.data
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch terms & conditions"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status
      })
    }
  }
)

export const saveTerms = createAsyncThunk(
  "terms/save",
  async (termsData, { rejectWithValue }) => {
    try {
      const response = await saveTermsApi(termsData)
      toastSuccess(response.data?.message || "Terms saved successfully")
      return response.data
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to save terms & conditions"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status
      })
    }
  }
)

export const deleteTerms = createAsyncThunk(
  "terms/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteTermsApi(id)
      toastSuccess(response.data?.message || "Terms deleted successfully")
      return { id, ...response.data }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete terms"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status
      })
    }
  }
)

const initialState = {
  terms: [], // Array of terms objects
  selectedTerm: null, // Currently selected term for editing/viewing
  loading: false,
  error: null,
  total: 0,
  lastUpdated: null,

  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },

  filters: {
    search: "",
    type: "", // e.g., "privacy", "terms", "refund", etc.
    isActive: true
  }
}

export const termsSlice = createSlice({
  name: "terms",
  initialState,
  reducers: {
    setTermFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      }
    },
    clearTermFilters: (state) => {
      state.filters = {
        search: "",
        type: "",
        isActive: true
      }
    },
    setSelectedTerm: (state, action) => {
      state.selectedTerm = action.payload
    },
    clearSelectedTerm: (state) => {
      state.selectedTerm = null
    },
    resetTermsState: (state) => {
      state.terms = []
      state.selectedTerm = null
      state.loading = false
      state.error = null
      state.total = 0
      state.lastUpdated = null
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      }
      state.filters = {
        search: "",
        type: "",
        isActive: true
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTerms.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTerms.fulfilled, (state, action) => {
        state.loading = false

        const payload = action.payload

        if (Array.isArray(payload)) {
          state.terms = payload
        } else if (payload && Array.isArray(payload.data)) {
          state.terms = payload.data
        } else if (payload && payload.data?.terms && Array.isArray(payload.data.terms)) {
          state.terms = payload.data.terms
        } else if (payload && Array.isArray(payload.terms)) {
          state.terms = payload.terms
        } else {
          state.terms = []
        }

        if (payload && payload.pagination) {
          state.pagination = {
            page: payload.pagination.page || 1,
            limit: payload.pagination.limit || 10,
            total: payload.pagination.total || state.terms.length,
            pages: payload.pagination.pages || 0
          }
        } else {
          state.pagination = {
            ...state.pagination,
            total: state.terms.length,
            pages: Math.ceil(state.terms.length / state.pagination.limit) || 0
          }
        }

        state.total = state.terms.length
        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(fetchTerms.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })

      .addCase(saveTerms.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(saveTerms.fulfilled, (state, action) => {
        state.loading = false

        const savedTerm = action.payload?.data || action.payload

        if (savedTerm) {
          const existingIndex = state.terms.findIndex(term => term._id === savedTerm._id)

          if (existingIndex !== -1) {
            state.terms[existingIndex] = savedTerm
          } else {
            state.terms.unshift(savedTerm)
            state.pagination.total = (state.pagination.total || 0) + 1
          }

          if (state.selectedTerm && state.selectedTerm._id === savedTerm._id) {
            state.selectedTerm = savedTerm
          }
        }

        state.total = state.terms.length
        state.pagination.pages = Math.ceil(state.pagination.total / state.pagination.limit) || 0

        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(saveTerms.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })

      .addCase(deleteTerms.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteTerms.fulfilled, (state, action) => {
        state.loading = false

        const deletedId = action.payload?.id || action.meta?.arg

        if (deletedId) {
          state.terms = state.terms.filter(term => term._id !== deletedId)

          if (state.selectedTerm && state.selectedTerm._id === deletedId) {
            state.selectedTerm = null
          }

          state.pagination.total = Math.max((state.pagination.total || 1) - 1, 0)
        }

        state.total = state.terms.length
        state.pagination.pages = Math.ceil(state.pagination.total / state.pagination.limit) || 0

        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(deleteTerms.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })
  }
})

export const {
  setTermFilters,
  clearTermFilters,
  setSelectedTerm,
  clearSelectedTerm,
  resetTermsState
} = termsSlice.actions

export const selectTerms = (state) => state.terms.terms
export const selectTermsLoading = (state) => state.terms.loading
export const selectTermsError = (state) => state.terms.error
export const selectTermsTotal = (state) => state.terms.total
export const selectSelectedTerm = (state) => state.terms.selectedTerm
export const selectTermsFilters = (state) => state.terms.filters
export const selectTermsPagination = (state) => state.terms.pagination

export default termsSlice.reducer